/**
 * Build a lookup map from normalised route paths to their titles.
 * Paths like "/canvision/*" are normalised to "/canvision".
 * @param {Array<{path: string, title?: string}>} routes
 * @returns {Map<string, string>}
 */
function buildRouteTitleMap(routes) {
	const map = new Map();
	if (!routes) return map;
	for (const route of routes) {
		if (route.path && route.title) {
			// Strip trailing "/*" or "*" so "/canvision/*" becomes "/canvision"
			const normalised = route.path.replace(/\/?\*$/, '').toLowerCase();
			if (normalised) map.set(normalised, route.title);
		}
	}
	return map;
}

/**
 * Format a raw URL segment into a human-readable label.
 * Replaces hyphens with spaces and title-cases each word.
 * e.g. "video-wall" -> "Video Wall", "contacts" -> "Contacts"
 * @param {string} segment
 * @returns {string}
 */
function formatSegment(segment) {
	return segment
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

/**
 * @function makeRouterLinks
 * @desc Creates an array of router links based on the provided location.
 *       When route data is supplied, breadcrumb labels are resolved from the
 *       route's `title` field first, falling back to title-cased URL segments.
 * @param {UseLocation} location The location object from the router.
 * @param {Array<{path: string, title?: string}>} [routes] Route config array (e.g. protectedRouteData).
 * @returns {Object[]} results
 * @returns {string} results.label
 * @returns {string} results.link
 */
function makeRouterLinks(location, routes) {
	const pathnames = location.pathname.split('/').filter((name) => name);
	const titleMap = buildRouteTitleMap(routes);

	const results = [{ label: 'Home', link: '/' }];

	if (pathnames.length > 0) {
		let accumulated = '';
		pathnames.forEach((element, index) => {
			accumulated += `/${element.toLowerCase()}`;
			const routeTitle = titleMap.get(accumulated);
			results[index + 1] = {
				label: routeTitle || formatSegment(element),
				link: accumulated,
			};
		});
	}

	return results;
}

export default makeRouterLinks;
